import React from 'react';
import { Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import type { ScoreBreakdown } from '@shared/utils/search';

const format = (value: number) => value.toFixed(3);

type SearchScoreDebugProps = Readonly<{
  score: ScoreBreakdown;
}>;

/**
 * Debug-only readout of a tile's search similarity, shown under the title when
 * the `showSearchScores` URL param is set. The headline number is the value that
 * gates and sorts the gallery; the tooltip breaks out the four inputs it maxes
 * over plus the concept-breadth tiebreak.
 *
 * Deliberately untranslated — engineers only, never shown to shoppers.
 */
export const SearchScoreDebug = ({ score }: SearchScoreDebugProps) => {
  // `lexical` is shown as `raw → weighted` because only the weighted value (the
  // 1.01 boost) is what `Math.max` sees, so it's the one that reconciles with
  // the headline `similarity`.
  const rows: ReadonlyArray<Readonly<{ factor: ScoreBreakdown['winningFactor']; label: string; value: string }>> = [
    { factor: 'lexical', label: 'lexical', value: `${format(score.lexical)} → ${format(score.weightedLexical)}` },
    { factor: 'concept', label: 'concept', value: format(score.concept) },
    { factor: 'color', label: 'color', value: format(score.color) },
    { factor: 'custom', label: 'custom', value: format(score.custom) },
  ];

  return (
    <TooltipV2
      placement="top"
      onMouseLeaveDelay={0}
      content={
        <Flex
          flexDirection="column"
          rowGap={1}
        >
          {rows.map(row => (
            <TextV2
              key={row.factor}
              typographyVariant="label2"
              fontWeight={row.factor === score.winningFactor ? 700 : 400}
            >
              {`${row.label}: ${row.value}`}
            </TextV2>
          ))}
          <TextV2 typographyVariant="label2">{`conceptSum: ${format(score.conceptSum)} (tiebreak)`}</TextV2>
        </Flex>
      }
    >
      <TextV2
        typographyVariant="label2"
        color="mono12"
        cursor="help"
      >
        {`${format(score.similarity)} · ${score.winningFactor}`}
      </TextV2>
    </TooltipV2>
  );
};
